import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import PhotoPicker from "@/components/PhotoPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

/**
 * Self-service profile page. Any logged-in user (admin or regular) can
 * update their own Full Name and Profile Photo. Username, role and status
 * are read-only here — those live behind the admin-only Users page.
 */
export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "",
    photo: user?.photo || "",
  });
  const [saving, setSaving] = useState(false);

  const dirty =
    (form.name || "") !== (user?.name || "") ||
    (form.photo || "") !== (user?.photo || "");

  const onSave = async (e) => {
    e.preventDefault();
    if (!dirty) return;
    setSaving(true);
    try {
      const { data } = await api.put("/users/me/profile", {
        name: form.name,
        photo: form.photo,
      });
      updateUser({ name: data.name, photo: data.photo });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="profile-page">
      <PageHeader
        overline="Account"
        title="My Profile"
        description="Update your display name and profile photo. Username, role and status are managed by an administrator."
        actions={
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => navigate(-1)}
            data-testid="profile-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-5 sm:py-8 max-w-2xl">
        <form
          onSubmit={onSave}
          className="border vsm-border vsm-surface p-5 sm:p-8 space-y-6"
          data-testid="profile-form"
        >
          <div>
            <Label className="vsm-overline">Profile Photo</Label>
            <div className="mt-2">
              <PhotoPicker
                testIdPrefix="profile-photo"
                size={96}
                value={form.photo}
                onChange={(v) => setForm({ ...form, photo: v })}
              />
            </div>
            <p className="text-xs vsm-muted mt-2">
              JPG or PNG, max ~900&thinsp;KB after compression. The image will be resized to 512&thinsp;px.
            </p>
          </div>

          <div>
            <Label className="vsm-overline">Username</Label>
            <Input
              disabled
              className="rounded-none mt-2 bg-zinc-100 dark:bg-zinc-800"
              value={user?.username || ""}
              data-testid="profile-username"
            />
            <p className="text-xs vsm-muted mt-1">Usernames cannot be changed.</p>
          </div>

          <div>
            <Label className="vsm-overline">
              Full Name <span className="text-red-600">*</span>
            </Label>
            <Input
              data-testid="profile-name"
              className="rounded-none mt-2"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              minLength={1}
              maxLength={120}
            />
          </div>

          <div>
            <Label className="vsm-overline">Role</Label>
            <Input
              disabled
              className="rounded-none mt-2 bg-zinc-100 dark:bg-zinc-800 uppercase"
              value={user?.role || ""}
              data-testid="profile-role"
            />
          </div>

          <div className="pt-2 flex items-center justify-end gap-2">
            <Button
              type="submit"
              disabled={!dirty || saving}
              className="rounded-none vsm-primary-bg hover:opacity-90"
              data-testid="profile-save"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
